// ============================================================================
// PsyReportView — результат отчёта методики КАРТОЧКАМИ (мобильная верстка):
// без таблиц и горизонтальных прокруток. Строки-детали (detail) — карточка с
// парами «реквизит → значение» (на телефоне в одну колонку, на десктопе в две),
// заголовки групп — подпись секции, итоги — отдельная сводка.
//
// Форматирование значений — единый lib/format (даты/числа по правилам базы).
// Пустой результат обрабатывает вызывающая панель (здесь только рендер строк).
// ============================================================================

import { useMemo } from 'react';
import { formatValue } from '@/lib/format';
import type { PsyReportResult, PsyReportRow } from '@/lib/psyReports';

function cellText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  if (typeof value === 'number') return formatValue(value, '', 'number');
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(text)) return formatValue(text, '', 'timestamp');
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return formatValue(text, '', 'date');
  return text;
}

function rowValues(row: PsyReportRow): Record<string, unknown> {
  return row.type === 'groupFooter' || row.type === 'grandTotal'
    ? (row.aggregates || {})
    : (row.data || {});
}

/** Заголовок группы: склеенные непустые значения измерения. */
function groupTitle(row: PsyReportRow, columns: string[]): string {
  const data = row.data || {};
  const parts = columns.map((c) => String(data[c] ?? '').trim()).filter((v) => v !== '');
  return parts.join(' · ') || 'Группа';
}

export function PsyReportView({ result }: { result: PsyReportResult }) {
  const columns = useMemo(() => result.columns.filter((c) => c !== ''), [result.columns]);

  return (
    <div className="space-y-2">
      {result.rows.map((row, index) => {
        if (row.type === 'groupHeader') {
          return (
            <div
              key={`h-${index}`}
              className="pt-1 text-xs font-medium text-foreground"
              style={{ paddingLeft: `${Math.min(row.level, 3) * 12}px` }}
            >
              {groupTitle(row, columns)}
            </div>
          );
        }
        if (row.type === 'groupFooter' || row.type === 'grandTotal') {
          const values = rowValues(row);
          const pairs = columns.filter((c) => values[c] !== undefined && values[c] !== null && values[c] !== '');
          if (pairs.length === 0) return null;
          return (
            <div key={`t-${index}`} className="rounded-lg border bg-muted/40 px-3 py-2 space-y-1">
              <div className="text-[11px] font-medium text-muted-foreground">
                {row.type === 'grandTotal' ? 'Итого' : 'Итог группы'}
              </div>
              <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
                {pairs.map((c) => (
                  <div key={c} className="flex items-baseline justify-between gap-2 text-xs">
                    <span className="text-muted-foreground truncate">{c}</span>
                    <span className="font-medium text-right break-words">{cellText(values[c])}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        }

        const values = row.data || {};
        const pairs = columns
          .map((c) => ({ column: c, value: values[c] }))
          .filter((p) => p.value !== null && p.value !== undefined && p.value !== '');
        if (pairs.length === 0) return null;
        return (
          <article key={`r-${index}`} className="rounded-lg border bg-card px-3 py-2">
            <dl className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
              {pairs.map((p) => (
                <div key={p.column} className="min-w-0 space-y-0.5">
                  <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{p.column}</dt>
                  <dd className="text-xs break-words">{cellText(p.value)}</dd>
                </div>
              ))}
            </dl>
          </article>
        );
      })}
    </div>
  );
}
